import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ThemePreset = "classic" | "warm-gold" | "navy-blue" | "modern-teal" | "custom";

export interface CustomColors {
  primary: string;
  accent: string;
}

interface ThemeColors {
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  ring: string;
}

interface ThemePresetConfig {
  name: string;
  description: string;
  colors: {
    light: ThemeColors;
    dark: ThemeColors;
  };
  preview: {
    primary: string;
    accent: string;
  };
}

interface ThemeSettingsValue {
  preset: ThemePreset;
  isDarkMode: boolean;
  customColors: CustomColors;
}

export const themePresets: Record<ThemePreset, ThemePresetConfig> = {
  classic: {
    name: "Classic B&W",
    description: "Timeless black and white with warm undertones",
    colors: {
      light: {
        primary: "0 0% 6%",
        primaryForeground: "0 0% 98%",
        accent: "30 5% 92%",
        accentForeground: "0 0% 6%",
        secondary: "30 5% 96%",
        secondaryForeground: "0 0% 6%",
        muted: "30 5% 94%",
        mutedForeground: "0 0% 40%",
        border: "30 5% 88%",
        ring: "0 0% 6%",
      },
      dark: {
        primary: "0 0% 98%",
        primaryForeground: "0 0% 6%",
        accent: "0 0% 15%",
        accentForeground: "0 0% 98%",
        secondary: "0 0% 12%",
        secondaryForeground: "0 0% 98%",
        muted: "0 0% 15%",
        mutedForeground: "0 0% 60%",
        border: "0 0% 18%",
        ring: "0 0% 83%",
      },
    },
    preview: {
      primary: "#0f0f0f",
      accent: "#eae8e5",
    },
  },
  "warm-gold": {
    name: "Warm Gold",
    description: "Elegant gold accents with rich warmth",
    colors: {
      light: {
        primary: "38 92% 40%",
        primaryForeground: "0 0% 100%",
        accent: "38 60% 90%",
        accentForeground: "38 92% 25%",
        secondary: "38 30% 95%",
        secondaryForeground: "38 50% 20%",
        muted: "38 20% 92%",
        mutedForeground: "38 20% 40%",
        border: "38 30% 85%",
        ring: "38 92% 40%",
      },
      dark: {
        primary: "38 80% 55%",
        primaryForeground: "20 40% 5%",
        accent: "20 40% 8%",
        accentForeground: "38 60% 80%",
        secondary: "18 45% 5%",
        secondaryForeground: "38 40% 90%",
        muted: "20 35% 7%",
        mutedForeground: "38 20% 50%",
        border: "22 30% 12%",
        ring: "38 80% 55%",
      },
    },
    preview: {
      primary: "#c48a12",
      accent: "#f5e6c8",
    },
  },
  "navy-blue": {
    name: "Navy Blue",
    description: "Professional deep blue palette",
    colors: {
      light: {
        primary: "222 47% 25%",
        primaryForeground: "0 0% 100%",
        accent: "222 30% 92%",
        accentForeground: "222 47% 20%",
        secondary: "222 20% 96%",
        secondaryForeground: "222 40% 20%",
        muted: "222 15% 94%",
        mutedForeground: "222 15% 45%",
        border: "222 20% 88%",
        ring: "222 47% 25%",
      },
      dark: {
        primary: "222 60% 60%",
        primaryForeground: "0 0% 100%",
        accent: "222 30% 18%",
        accentForeground: "222 50% 80%",
        secondary: "222 25% 12%",
        secondaryForeground: "222 30% 90%",
        muted: "222 20% 15%",
        mutedForeground: "222 15% 60%",
        border: "222 20% 22%",
        ring: "222 60% 60%",
      },
    },
    preview: {
      primary: "#22355c",
      accent: "#e1e6f0",
    },
  },
  "modern-teal": {
    name: "Modern Teal",
    description: "Fresh and contemporary teal tones",
    colors: {
      light: {
        primary: "174 62% 35%",
        primaryForeground: "0 0% 100%",
        accent: "174 40% 90%",
        accentForeground: "174 62% 25%",
        secondary: "174 25% 95%",
        secondaryForeground: "174 50% 20%",
        muted: "174 20% 93%",
        mutedForeground: "174 15% 40%",
        border: "174 25% 85%",
        ring: "174 62% 35%",
      },
      dark: {
        primary: "174 55% 50%",
        primaryForeground: "0 0% 5%",
        accent: "174 35% 18%",
        accentForeground: "174 45% 80%",
        secondary: "174 25% 12%",
        secondaryForeground: "174 35% 90%",
        muted: "174 20% 15%",
        mutedForeground: "174 15% 60%",
        border: "174 20% 22%",
        ring: "174 55% 50%",
      },
    },
    preview: {
      primary: "#228b8b",
      accent: "#d4eded",
    },
  },
  custom: {
    name: "Custom",
    description: "Set your own brand colors",
    colors: {
      light: {
        primary: "0 0% 6%",
        primaryForeground: "0 0% 98%",
        accent: "30 5% 92%",
        accentForeground: "0 0% 6%",
        secondary: "30 5% 96%",
        secondaryForeground: "0 0% 6%",
        muted: "30 5% 94%",
        mutedForeground: "0 0% 40%",
        border: "30 5% 88%",
        ring: "0 0% 6%",
      },
      dark: {
        primary: "0 0% 98%",
        primaryForeground: "0 0% 6%",
        accent: "0 0% 15%",
        accentForeground: "0 0% 98%",
        secondary: "0 0% 12%",
        secondaryForeground: "0 0% 98%",
        muted: "0 0% 15%",
        mutedForeground: "0 0% 60%",
        border: "0 0% 18%",
        ring: "0 0% 83%",
      },
    },
    preview: {
      primary: "#0f0f0f",
      accent: "#eae8e5",
    },
  },
};

interface ThemeContextType {
  currentTheme: ThemePreset;
  setTheme: (theme: ThemePreset) => void;
  isDarkMode: boolean;
  customColors: CustomColors;
  setCustomColors: (colors: CustomColors) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to convert hex to HSL
function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0 0% 0%";
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Generate theme colors from a primary hex color
function generateColorsFromHex(primaryHex: string, accentHex: string): { light: ThemeColors; dark: ThemeColors } {
  const primaryHsl = hexToHsl(primaryHex);
  const accentHsl = hexToHsl(accentHex);
  
  // Parse the HSL values
  const [pH, pS, pL] = primaryHsl.split(" ").map((v, i) => i === 0 ? parseInt(v) : parseInt(v));
  const [aH, aS] = accentHsl.split(" ").map((v, i) => i === 0 ? parseInt(v) : parseInt(v));
  
  return {
    light: {
      primary: primaryHsl,
      primaryForeground: pL > 50 ? "0 0% 6%" : "0 0% 98%",
      accent: `${aH} ${Math.min(aS, 40)}% 92%`,
      accentForeground: `${pH} ${pS}% 25%`,
      secondary: `${pH} ${Math.min(pS, 30)}% 96%`,
      secondaryForeground: `${pH} ${Math.min(pS, 50)}% 20%`,
      muted: `${pH} ${Math.min(pS, 20)}% 94%`,
      mutedForeground: `${pH} ${Math.min(pS, 20)}% 40%`,
      border: `${pH} ${Math.min(pS, 25)}% 88%`,
      ring: primaryHsl,
    },
    dark: {
      primary: `${pH} ${Math.min(pS, 70)}% 60%`,
      primaryForeground: "0 0% 5%",
      accent: `${aH} ${Math.min(aS, 40)}% 18%`,
      accentForeground: `${aH} ${Math.min(aS, 50)}% 80%`,
      secondary: `${pH} ${Math.min(pS, 25)}% 12%`,
      secondaryForeground: `${pH} ${Math.min(pS, 35)}% 90%`,
      muted: `${pH} ${Math.min(pS, 20)}% 15%`,
      mutedForeground: `${pH} ${Math.min(pS, 15)}% 60%`,
      border: `${pH} ${Math.min(pS, 20)}% 22%`,
      ring: `${pH} ${Math.min(pS, 70)}% 60%`,
    },
  };
}

function applyThemeColors(preset: ThemePreset, isDark: boolean, customColors?: CustomColors) {
  let colors: ThemeColors;
  
  if (preset === "custom" && customColors) {
    const generated = generateColorsFromHex(customColors.primary, customColors.accent);
    colors = generated[isDark ? "dark" : "light"];
  } else {
    colors = themePresets[preset].colors[isDark ? "dark" : "light"];
  }
  
  const root = document.documentElement;
  
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--primary-foreground", colors.primaryForeground);
  root.style.setProperty("--accent", colors.accent);
  root.style.setProperty("--accent-foreground", colors.accentForeground);
  root.style.setProperty("--secondary", colors.secondary);
  root.style.setProperty("--secondary-foreground", colors.secondaryForeground);
  root.style.setProperty("--muted", colors.muted);
  root.style.setProperty("--muted-foreground", colors.mutedForeground);
  root.style.setProperty("--border", colors.border);
  root.style.setProperty("--ring", colors.ring);
  root.style.setProperty("--input", colors.border);
  
  // Also update sidebar colors
  root.style.setProperty("--sidebar-primary", colors.primary);
  root.style.setProperty("--sidebar-primary-foreground", colors.primaryForeground);
  root.style.setProperty("--sidebar-accent", colors.accent);
  root.style.setProperty("--sidebar-accent-foreground", colors.accentForeground);
  root.style.setProperty("--sidebar-border", colors.border);
  root.style.setProperty("--sidebar-ring", colors.ring);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<ThemePreset>("classic");
  const [customColors, setCustomColorsState] = useState<CustomColors>({ primary: "#0f0f0f", accent: "#eae8e5" });
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return document.documentElement.classList.contains("dark");
    }
    return false;
  });

  // Fetch theme settings from database
  const fetchThemeSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "theme")
        .single();
      
      if (error) {
        console.error("Error fetching theme settings:", error);
        return;
      }

      if (data?.value) {
        const settings = data.value as unknown as ThemeSettingsValue;
        if (settings.preset && settings.preset in themePresets) {
          setCurrentTheme(settings.preset);
        }
        if (settings.customColors) {
          setCustomColorsState(settings.customColors);
        }
      }
    } catch (error) {
      console.error("Error fetching theme settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save theme settings to database
  const saveThemeSettings = useCallback(async (preset: ThemePreset, colors: CustomColors) => {
    try {
      const newValue = JSON.parse(JSON.stringify({
        preset,
        isDarkMode,
        customColors: {
          primary: colors.primary,
          accent: colors.accent,
        },
      }));

      const { error } = await supabase
        .from("site_settings")
        .update({ value: newValue })
        .eq("key", "theme");

      if (error) {
        console.error("Error saving theme settings:", error);
      }
    } catch (error) {
      console.error("Error saving theme settings:", error);
    }
  }, [isDarkMode]);

  // Subscribe to realtime updates for theme changes
  useEffect(() => {
    fetchThemeSettings();

    const channel = supabase
      .channel("site_settings_changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "site_settings",
          filter: "key=eq.theme",
        },
        (payload) => {
          const settings = payload.new.value as unknown as ThemeSettingsValue;
          if (settings.preset && settings.preset in themePresets) {
            setCurrentTheme(settings.preset);
          }
          if (settings.customColors) {
            setCustomColorsState(settings.customColors);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchThemeSettings]);

  // Watch for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains("dark");
      setIsDarkMode(isDark);
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    
    return () => observer.disconnect();
  }, []);

  // Apply theme colors when theme, dark mode, or custom colors change
  useEffect(() => {
    applyThemeColors(currentTheme, isDarkMode, customColors);
  }, [currentTheme, isDarkMode, customColors]);

  const setTheme = useCallback((theme: ThemePreset) => {
    setCurrentTheme(theme);
    saveThemeSettings(theme, customColors);
  }, [customColors, saveThemeSettings]);

  const setCustomColors = useCallback((colors: CustomColors) => {
    setCustomColorsState(colors);
    saveThemeSettings(currentTheme, colors);
  }, [currentTheme, saveThemeSettings]);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, isDarkMode, customColors, setCustomColors, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
